import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/http/client'
import { CAMPAIGN_AI_REQUEST_TIMEOUT_MS } from '@/api/http/requestTimeout'
import { queryKeys } from '@/api/query-keys'
import { captureSmCampaignLaunched } from '@/lib/analytics'
import { useSessionStore } from '@/stores/useSessionStore'
import type { ApiResponse } from '@/api/http/types'
import type { Campaign, CampaignInstrument } from '../types'

function tenantKey(tenantId: string | null | undefined) {
  return tenantId ?? '_'
}

export type CreateCampaignPayload = {
  name: string
  researchGoal: string
  budget?: number | null
  startDate?: string | null
  endDate?: string | null
  targetAudience: string
  status: 'draft' | 'inactive'
  wizardStep?: number
  companyId?: string | null
}

/** Optional abort — passed in mutation variables (not MutateOptions) for TanStack Query v5 typings. */
export type MutationAbortOptions = {
  signal?: AbortSignal
}

export type CreateCampaignVariables = CreateCampaignPayload & MutationAbortOptions

export type CompleteStep1Variables = {
  campaignId: string
  body: {
    name: string
    researchGoal: string
    budget?: number | null
    startDate: string
    endDate: string
    targetAudience: string
  }
} & MutationAbortOptions

export type CompleteStep2Variables = {
  campaignId: string
  body: { clarifyingAnswers?: Record<string, string> }
} & MutationAbortOptions

/** Persists a new campaign (no empty shell). Used from setup when saving draft or before step 1 complete. */
export function useCreateCampaign() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    retry: false,
    mutationFn: async (variables: CreateCampaignVariables) => {
      const { signal, ...body } = variables
      const { data: envelope } = await apiClient.post<ApiResponse<Campaign>>(
        '/api/v1/campaigns',
        {
          ...body,
          startDate: body.startDate || null,
          endDate: body.endDate || null,
        },
        {
          signal,
          ...(signal
            ? { timeout: CAMPAIGN_AI_REQUEST_TIMEOUT_MS }
            : undefined),
        },
      )
      return envelope.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.all(tenantKey(t)),
      })
    },
  })
}

export function usePatchCampaign() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async (args: {
      campaignId: string
      body: {
        name?: string
        researchGoal?: string
        budget?: number | null
        startDate?: string | null
        endDate?: string | null
        targetAudience?: string
        status?:
          | 'draft'
          | 'inactive'
          | 'goal_analysis'
          | 'lines_of_inquiry'
          | 'active'
        wizardStep?: number
        clarifyingAnswers?: Record<string, string>
      }
    }) => {
      const { data: envelope } = await apiClient.patch<ApiResponse<Campaign>>(
        `/api/v1/campaigns/${args.campaignId}`,
        args.body,
      )
      return envelope.data
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        queryKeys.campaigns.detail(tenantKey(t), variables.campaignId),
        data,
      )
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.all(tenantKey(t)),
      })
    },
  })
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: envelope } = await apiClient.delete<ApiResponse<null>>(
        `/api/v1/campaigns/${id}`,
      )
      return envelope.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.all(tenantKey(t)),
      })
    },
  })
}

export function useCompleteStep1() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    retry: false,
    mutationFn: async (variables: CompleteStep1Variables) => {
      const { signal, campaignId, body } = variables
      const { data: envelope } = await apiClient.post<ApiResponse<Campaign>>(
        `/api/v1/campaigns/${campaignId}/step-1/complete`,
        {
          ...body,
          startDate: new Date(body.startDate).toISOString(),
          endDate: new Date(body.endDate).toISOString(),
        },
        { timeout: CAMPAIGN_AI_REQUEST_TIMEOUT_MS, signal },
      )
      return envelope.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.all(tenantKey(t)),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.detail(
          tenantKey(t),
          variables.campaignId,
        ),
      })
    },
  })
}

export function useCompleteStep2() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    retry: false,
    mutationFn: async (variables: CompleteStep2Variables) => {
      const { signal, campaignId, body } = variables
      const { data: envelope } = await apiClient.post<ApiResponse<Campaign>>(
        `/api/v1/campaigns/${campaignId}/step-2/complete`,
        body,
        { timeout: CAMPAIGN_AI_REQUEST_TIMEOUT_MS, signal },
      )
      return envelope.data
    },
    onSuccess: (data, variables) => {
      const detailKey = queryKeys.campaigns.detail(
        tenantKey(t),
        variables.campaignId,
      )
      queryClient.setQueryData<Campaign>(detailKey, (previous) =>
        previous ? { ...previous, ...data } : data,
      )
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.all(tenantKey(t)),
      })
    },
  })
}

export type UpdateLoiPayload = {
  title?: string
  description?: string
}

/** PATCH `/api/v1/campaigns/:id/loi/:loiId` — at least one of title or description */
export function useUpdateLoi() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async (args: {
      campaignId: string
      loiId: string
      body: UpdateLoiPayload
    }) => {
      const { data: envelope } = await apiClient.patch<ApiResponse<Campaign>>(
        `/api/v1/campaigns/${args.campaignId}/loi/${args.loiId}`,
        args.body,
      )
      return envelope.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.all(tenantKey(t)),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.detail(
          tenantKey(t),
          variables.campaignId,
        ),
      })
    },
  })
}

export function useDeleteLoi() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async (args: { campaignId: string; loiId: string }) => {
      const { data: envelope } = await apiClient.delete<ApiResponse<Campaign>>(
        `/api/v1/campaigns/${args.campaignId}/loi/${args.loiId}`,
      )
      return envelope.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.all(tenantKey(t)),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.detail(
          tenantKey(t),
          variables.campaignId,
        ),
      })
    },
  })
}

export function useReorderLoi() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async (args: {
      campaignId: string
      orderedIds: string[]
    }) => {
      const { data: envelope } = await apiClient.patch<ApiResponse<Campaign>>(
        `/api/v1/campaigns/${args.campaignId}/loi/reorder`,
        { orderedIds: args.orderedIds },
      )
      return envelope.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.all(tenantKey(t)),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.detail(
          tenantKey(t),
          variables.campaignId,
        ),
      })
    },
  })
}

export function useLaunchCampaign() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async (args: {
      campaignId: string
      body?: { teamMemberIds?: string[] }
    }) => {
      const { data: envelope } = await apiClient.post<ApiResponse<Campaign>>(
        `/api/v1/campaigns/${args.campaignId}/launch`,
        args.body ?? {},
      )
      return envelope.data
    },
    onSuccess: (_data, variables) => {
      const smUserId = useSessionStore.getState().user?.id ?? null
      const teamMemberCount = variables.body?.teamMemberIds?.length
      captureSmCampaignLaunched({
        campaign_id: variables.campaignId,
        sm_user_id: smUserId,
        ...(teamMemberCount !== undefined ? { team_member_count: teamMemberCount } : {}),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.all(tenantKey(t)),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.detail(
          tenantKey(t),
          variables.campaignId,
        ),
      })
    },
  })
}

export function useUnlockLoi() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async (args: { campaignId: string; loiId: string }) => {
      const { data: envelope } = await apiClient.post<ApiResponse<Campaign>>(
        `/api/v1/campaigns/${args.campaignId}/loi/${args.loiId}/unlock`,
        {},
      )
      return envelope.data
    },
    onSuccess: (_data, variables) => {
      const tk = tenantKey(t)
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all(tk) })
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.detail(tk, variables.campaignId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.instruments(tk, variables.campaignId),
      })
    },
  })
}

export function useReactivateInstrument() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async (args: {
      campaignId: string
      instrumentId: string
      loiId?: string
    }) => {
      const { data: envelope } = await apiClient.patch<
        ApiResponse<CampaignInstrument>
      >(
        `/api/v1/instruments/${args.instrumentId}/reactivate`,
      )
      return envelope.data
    },
    onSuccess: (_data, variables) => {
      const tk = tenantKey(t)
      queryClient.invalidateQueries({
        queryKey: queryKeys.instruments.list(tk),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.instruments.all(tk),
      })
      if (variables.campaignId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.campaigns.instruments(tk, variables.campaignId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.campaigns.detail(tk, variables.campaignId),
        })
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.instrumentDashboard.summary(tk, variables.instrumentId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.loiDashboard.all(tk),
      })
      if (variables.loiId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.loiDashboard.instruments(tk, variables.loiId, 'all'),
        })
      }
    },
  })
}

export function useUpdateCampaignDuration() {
  const queryClient = useQueryClient()
  const t = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async (args: { campaignId: string; endDate: string }) => {
      const { data: envelope } = await apiClient.patch<ApiResponse<Campaign>>(
        `/api/v1/campaigns/${args.campaignId}/duration`,
        { endDate: args.endDate },
      )
      return envelope.data
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        queryKeys.campaigns.detail(tenantKey(t), variables.campaignId),
        data,
      )
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.all(tenantKey(t)),
      })
    },
  })
}
